import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { MouseEvent, RefObject } from "react";
import { IconChevronRight, IconFolderOpen } from "@tabler/icons-react";
import clsx from "clsx";

import { useFileManager } from "@/components/core/FileManager";
import type {
  BreadcrumbEntry,
  FileManagerItem,
  FileManagerQueryContext,
} from "@/components/core/FileManager";
import { FileManagerItemContextMenu } from "@/components/core/FileManager";
import { FileManagerItemDndWrapper } from "@/components/core/FileManager";
import { FileManagerTreeItemMenuItems } from "../FileManagerContextMenu/FileManagerTreeItemMenuItems";
import { getIconConfig } from "../FileManagerItemCard/FileManagerItemCard";

import styles from "./FileManagerTreeView.module.scss";

export interface FileManagerTreeViewProps {
  fetchItems: (ctx: FileManagerQueryContext) => Promise<FileManagerItem[]>;
  defaultExpandedIds?: string[];
}

interface TreeNodeShared {
  expanded: Set<string>;
  nodeChildren: Map<string, FileManagerItem[]>;
  nodeLoading: Set<string>;
  currentFolderId: string | null;
  onToggle: (item: FileManagerItem) => void;
  onNavigate: (item: FileManagerItem) => void;
  treeRef: RefObject<HTMLDivElement | null>;
}

interface TreeNodeProps extends TreeNodeShared {
  item: FileManagerItem;
  depth: number;
}

function buildItemPath(
  item: FileManagerItem,
  rootItems: FileManagerItem[],
  nodeChildren: Map<string, FileManagerItem[]>,
): BreadcrumbEntry[] {
  const idToItem = new Map<string, FileManagerItem>();
  rootItems.forEach((i) => idToItem.set(i.id, i));
  nodeChildren.forEach((children) => children.forEach((i) => idToItem.set(i.id, i)));

  const path: BreadcrumbEntry[] = [];
  let current: FileManagerItem | undefined = item;
  while (current) {
    path.unshift({ id: current.id, name: current.name });
    current = current.parentId ? idToItem.get(current.parentId) : undefined;
  }
  return path;
}

function focusSibling(treeRef: RefObject<HTMLDivElement | null>, from: HTMLElement, dir: 1 | -1) {
  const nodes = [...(treeRef.current?.querySelectorAll<HTMLElement>("[data-tree-item]") ?? [])];
  const idx = nodes.indexOf(from);
  nodes[idx + dir]?.focus();
}

function TreeNode({
  item,
  depth,
  expanded,
  nodeChildren,
  nodeLoading,
  currentFolderId,
  onToggle,
  onNavigate,
  treeRef,
}: TreeNodeProps) {
  const isExpanded = expanded.has(item.id);
  const isActive = currentFolderId === item.id;
  const isLoading = nodeLoading.has(item.id);
  const children = nodeChildren.get(item.id) ?? [];
  const hasChildren = item.childCount > 0;
  const { Icon, color } = getIconConfig(item);

  const shared: TreeNodeShared = {
    expanded,
    nodeChildren,
    nodeLoading,
    currentFolderId,
    onToggle,
    onNavigate,
    treeRef,
  };

  function handleChevronClick(e: MouseEvent) {
    e.stopPropagation();
    onToggle(item);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        focusSibling(treeRef, e.currentTarget, 1);
        break;
      case "ArrowUp":
        e.preventDefault();
        focusSibling(treeRef, e.currentTarget, -1);
        break;
      case "ArrowRight":
        e.preventDefault();
        if (!isExpanded && hasChildren) {
          onToggle(item);
        } else if (isExpanded) {
          focusSibling(treeRef, e.currentTarget, 1);
        }
        break;
      case "ArrowLeft":
        e.preventDefault();
        if (isExpanded) {
          onToggle(item);
        }
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        onNavigate(item);
        break;
    }
  }

  return (
    <div>
      <FileManagerItemDndWrapper item={item} idPrefix="tree-">
        <FileManagerItemContextMenu
          item={item}
          menuContent={<FileManagerTreeItemMenuItems item={item} />}
        >
          <button
            type="button"
            data-tree-item
            className={clsx(styles.button, isActive && styles.buttonActive)}
            style={{ paddingLeft: depth * 14 + 6 }}
            onClick={() => onNavigate(item)}
            onKeyDown={handleKeyDown}
            aria-expanded={hasChildren ? isExpanded : undefined}
          >
            <span className={styles.chevronSlot} onClick={handleChevronClick}>
              {hasChildren &&
                (isLoading && !children.length ? (
                  <span className={styles.spinner} />
                ) : (
                  <IconChevronRight
                    size={11}
                    className={clsx(styles.chevron, isExpanded && styles.chevronExpanded)}
                  />
                ))}
            </span>
            <Icon size={14} color={isActive ? "var(--mantine-color-blue-7, #1c7ed6)" : color} />
            <span className={styles.itemName}>{item.name}</span>
          </button>
        </FileManagerItemContextMenu>
      </FileManagerItemDndWrapper>

      {isExpanded && (
        <div>
          {children.map((child) => (
            <TreeNode key={child.id} item={child} depth={depth + 1} {...shared} />
          ))}
        </div>
      )}
    </div>
  );
}

export function FileManagerTreeView({ fetchItems, defaultExpandedIds }: FileManagerTreeViewProps) {
  const {
    currentFolderId,
    breadcrumb,
    openFolder,
    items: contextItems,
    mutationVersion,
  } = useFileManager();

  const [rootItems, setRootItems] = useState<FileManagerItem[]>([]);
  const [rootLoading, setRootLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(defaultExpandedIds));
  const [nodeChildren, setNodeChildren] = useState<Map<string, FileManagerItem[]>>(new Map());
  const [nodeLoading, setNodeLoading] = useState<Set<string>>(new Set());

  const fetchItemsRef = useRef(fetchItems);
  const fetchedRef = useRef<Set<string>>(new Set());
  const expandedRef = useRef(expanded);
  const treeRef = useRef<HTMLDivElement>(null);
  const prevMutationVersionRef = useRef(mutationVersion);

  useLayoutEffect(() => {
    fetchItemsRef.current = fetchItems;
  });

  useLayoutEffect(() => {
    expandedRef.current = expanded;
  });

  function fetchNode(folderId: string) {
    setNodeLoading((prev) => new Set([...prev, folderId]));
    fetchItemsRef
      .current({ currentFolderId: folderId, breadcrumb: [], search: null })
      .then((items) => {
        setNodeChildren(
          (prev) => new Map([...prev, [folderId, items.filter((i) => i.type === "FOLDER")]]),
        );
        setNodeLoading((prev) => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      })
      .catch(() => {
        setNodeLoading((prev) => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      });
  }

  function fetchChildren(folderId: string) {
    if (fetchedRef.current.has(folderId)) return;
    fetchedRef.current.add(folderId);
    fetchNode(folderId);
  }

  useEffect(() => {
    let cancelled = false;
    setRootLoading(true);
    fetchItemsRef
      .current({ currentFolderId: null, breadcrumb: [], search: null })
      .then((items) => {
        if (cancelled) return;
        setRootItems(items.filter((i) => i.type === "FOLDER"));
        setRootLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setRootLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const ids = breadcrumb.map((e) => e.id);
    if (ids.length === 0) return;
    setExpanded((prev) => new Set([...prev, ...ids]));
    ids.forEach((id) => fetchChildren(id));
  }, [breadcrumb]);

  useEffect(() => {
    const folders = contextItems.filter((i) => i.type === "FOLDER");
    if (currentFolderId === null) {
      setRootItems(folders);
      setRootLoading(false);
    } else {
      setNodeChildren((prev) => {
        if (!prev.has(currentFolderId)) return prev;
        return new Map([...prev, [currentFolderId, folders]]);
      });
    }
  }, [contextItems, currentFolderId]);

  useEffect(() => {
    if (mutationVersion === prevMutationVersionRef.current) return;
    prevMutationVersionRef.current = mutationVersion;

    fetchedRef.current.clear();

    fetchItemsRef
      .current({ currentFolderId: null, breadcrumb: [], search: null })
      .then((items) => setRootItems(items.filter((i) => i.type === "FOLDER")))
      .catch(() => {});

    expandedRef.current.forEach((id) => fetchChildren(id));
  }, [mutationVersion]);

  const handleToggle = (item: FileManagerItem) => {
    const isExpanded = expanded.has(item.id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (isExpanded) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      return next;
    });
    if (!isExpanded) {
      fetchChildren(item.id);
    }
  };

  const handleNavigate = (item: FileManagerItem) => {
    openFolder(item.id, item.name, buildItemPath(item, rootItems, nodeChildren));
    if (item.childCount > 0 && !expanded.has(item.id)) {
      setExpanded((prev) => new Set([...prev, item.id]));
      fetchChildren(item.id);
    }
  };

  const shared: TreeNodeShared = {
    expanded,
    nodeChildren,
    nodeLoading,
    currentFolderId,
    onToggle: handleToggle,
    onNavigate: handleNavigate,
    treeRef,
  };

  function handleRootKeyDown(e: React.KeyboardEvent<HTMLButtonElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusSibling(treeRef, e.currentTarget, 1);
    }
  }

  return (
    <div ref={treeRef} className={styles.tree} role="tree">
      <button
        type="button"
        data-tree-item
        className={clsx(styles.button, currentFolderId === null && styles.buttonActive)}
        style={{ paddingLeft: 6 }}
        onClick={() => openFolder(null)}
        onKeyDown={handleRootKeyDown}
      >
        <span className={styles.chevronSlot} />
        <IconFolderOpen
          size={14}
          color={currentFolderId === null ? "var(--mantine-color-blue-7, #1c7ed6)" : "#4C6EF5"}
        />
        <span className={styles.itemName}>Root</span>
      </button>

      {rootLoading ? (
        <div className={styles.rootLoading}>
          <span className={styles.spinner} />
        </div>
      ) : (
        rootItems.map((item) => <TreeNode key={item.id} item={item} depth={1} {...shared} />)
      )}
    </div>
  );
}

FileManagerTreeView.displayName = "FileManagerTreeView";
